import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import { useNotify } from '@/components/feedback/notificationsContext';
import { updateConsent } from './api';
import { NO_CONSENT, phoneValid, wantsPhone } from './consent';
import { ConsentOptions } from './ConsentOptions';
import { useSession } from './session';
import type { Consent } from './types';
import { IndependenceNote } from '@/components/IndependenceNote';

const BUTTON_LABEL = 'Novidades e comunicações';

/**
 * At the bottom of every page: a discreet button that opens the opt-ins. For whoever never
 * answered (me.consentAnswered), the dialog opens by itself once and says where the button is;
 * "Agora não" also counts as an answer, so it does not ask again.
 */
export function ConsentFooter() {
  const { me, user, refresh } = useSession();
  const viewing = Boolean(me.viewer);
  // First visit since the question exists: ask right away (never while seeing as someone else).
  const [open, setOpen] = useState(!me.consentAnswered && !viewing);
  const [first, setFirst] = useState(!me.consentAnswered);

  return (
    <Box component="footer" sx={{ textAlign: 'center', pb: { xs: 3, md: 10 } }}>
      <Button
        size="small"
        color="inherit"
        startIcon={<MailOutlineRounded fontSize="small" />}
        onClick={() => setOpen(true)}
        sx={{ color: 'text.secondary', fontWeight: 400 }}
      >
        {BUTTON_LABEL}
      </Button>
      <IndependenceNote sx={{ mt: 0.5 }} />
      {open && (
        <ConsentDialog
          initial={me.consent ?? NO_CONSENT}
          email={user.email}
          first={first}
          readOnly={viewing}
          onDone={(saved) => {
            setOpen(false);
            setFirst(false);
            if (saved) void refresh();
          }}
        />
      )}
    </Box>
  );
}

function ConsentDialog({
  initial,
  email,
  first,
  readOnly,
  onDone,
}: {
  initial: Consent;
  email: string;
  /** Opened by itself: explain where to change it later; closing answers "no". */
  first: boolean;
  readOnly: boolean;
  onDone: (saved: boolean) => void;
}) {
  const notify = useNotify();
  const [value, setValue] = useState<Consent>(initial);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const missingPhone = wantsPhone(value) && !phoneValid(value.phone);

  const save = async (next: Consent, message: string | null) => {
    setSaving(true);
    try {
      await updateConsent(next);
      if (message) notify(message, 'success');
      onDone(true);
    } catch {
      notify('Não foi possível salvar suas preferências.', 'error');
      setSaving(false);
    }
  };

  const submit = () => {
    setTried(true);
    if (!missingPhone) void save(value, 'Preferências de comunicação salvas.');
  };

  // The first time, "not now" is an answer too (nothing ticked), so the question does not return.
  const dismiss = () => {
    if (saving) return;
    if (first && !readOnly) void save(initial, null);
    else onDone(false);
  };

  return (
    <Dialog open onClose={dismiss} fullWidth maxWidth="sm">
      <DialogTitle>Quer receber novidades?</DialogTitle>
      <DialogContent>
        {first && (
          <Alert severity="info" icon={<MailOutlineRounded />} sx={{ mb: 2 }}>
            Você escolhe agora e pode mudar quando quiser: no fim de qualquer página do Lousa, em{' '}
            <b>{BUTTON_LABEL}</b>. Nada vem marcado.
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Marque o que quiser receber e como.
        </Typography>
        <ConsentOptions
          value={value}
          onChange={setValue}
          email={email}
          disabled={saving || readOnly}
          phoneError={
            tried && missingPhone
              ? 'Informe o telefone com DDD para receber por telefone.'
              : undefined
          }
        />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={dismiss} disabled={saving}>
          {first && !readOnly ? 'Agora não' : 'Fechar'}
        </Button>
        {!readOnly && (
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
